import { type FormEvent, useId, useState } from 'react'
import { CalendarCheck, Camera, Mail, Phone, Route } from 'lucide-react'
import { ceramicPackages, formatCurrencyFromCents } from '../../data/protection-catalog'
import { callSupabaseFunction } from '../../lib/api'
import {
  persistSelectedProtection,
  resolveSelectedProtection,
} from '../../lib/selected-protection'
import { siteConfig } from '../../lib/site-config'

export interface ContactSectionProps {}

export function ContactSection(_: Readonly<ContactSectionProps>) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(siteConfig.address)}`
  const nameId = useId()
  const phoneId = useId()
  const emailId = useId()
  const serviceId = useId()
  const messageId = useId()
  const [selectedProtection] = useState(() => {
    if (typeof window === 'undefined') return null
    const resolved = resolveSelectedProtection({
      search: window.location.search,
      packages: ceramicPackages,
    })
    if (resolved) persistSelectedProtection(resolved)
    return resolved
  })
  const protectionSummary = selectedProtection
    ? `${selectedProtection.durationLabel} (${selectedProtection.productName}), from ${formatCurrencyFromCents(selectedProtection.startingPriceCents)} inc. GST`
    : null

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const payload = Object.fromEntries(
      [...new FormData(form).entries()].map(([key, value]) => [key, String(value)]),
    )
    const message = payload.message ?? ''
    if (selectedProtection && !message.includes(selectedProtection.productName)) {
      payload.message = `${message}\n\nSelected protection: ${protectionSummary}.`
    }
    if (selectedProtection) {
      payload.protectionId = selectedProtection.id
    }

    setStatus('sending')

    try {
      const response = await callSupabaseFunction('contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!response.ok) throw new Error('Contact request failed')

      form.reset()
      setStatus('sent')
    } catch {
      setStatus('error')
    }
  }

  return (
    <section className="contact-section" id="contact-form" aria-labelledby="contact-heading">
      <img className="contact-bg" src="/images/home-screen-image.webp" alt="" aria-hidden="true" loading="lazy" />
      <div className="section-container contact-grid">
        <div>
          <p className="eyebrow">Studio Contact</p>
          <h1 id="contact-heading">Send us a message</h1>
          <p className="contact-intro">
            Ask about ceramic coating, paint correction or tailored detailing. We will reply by phone or email.
          </p>
          {selectedProtection ? (
            <p className="selected-protection-note" role="status">
              Selected protection: {protectionSummary}.
            </p>
          ) : null}
          <form className="contact-form" onSubmit={handleSubmit}>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" hidden />
            {selectedProtection ? (
              <input type="hidden" name="protectionId" value={selectedProtection.id} />
            ) : null}
            <div>
              <label htmlFor={nameId}>
                Name
                <input id={nameId} type="text" name="name" autoComplete="name" required />
              </label>
              <label htmlFor={phoneId}>
                Phone
                <input id={phoneId} type="tel" name="phone" autoComplete="tel" required />
              </label>
            </div>
            <label htmlFor={emailId}>
              Email
              <input id={emailId} type="email" name="email" autoComplete="email" required />
            </label>
            <label htmlFor={serviceId}>
              Service
              <select id={serviceId} name="service" defaultValue={selectedProtection ? 'Ceramic Protection' : ''}>
                <option value="" disabled>Select service</option>
                <option value="General enquiry">General enquiry</option>
                <option value="General Detailing">General Detailing</option>
                <option value="Ceramic Protection">Ceramic Protection</option>
                <option value="Paint Correction">Paint Correction</option>
              </select>
            </label>
            <label htmlFor={messageId}>
              Message
              <textarea
                id={messageId}
                name="message"
                placeholder="Tell us about your vehicle and what you need."
                rows={5}
                required
                defaultValue={
                  selectedProtection
                    ? `I would like a quote for ${protectionSummary}.`
                    : undefined
                }
              />
            </label>
            <button type="submit" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending...' : 'Send Message'}
            </button>
            <p className={`contact-form-status ${status}`} aria-live="polite">
              {status === 'sent'
                ? 'Thanks, your enquiry has been sent. Check your email for a confirmation.'
                : null}
              {status === 'error'
                ? `Something went wrong. Call ${siteConfig.phone} or email ${siteConfig.email}.`
                : null}
            </p>
          </form>
        </div>

        <aside className="contact-card" aria-label="Studio details">
          <div className="studio-detail-block">
            <h2>Studio Address</h2>
            <p>{siteConfig.address}</p>
            <a href={mapsUrl}>
              <Route size={18} aria-hidden="true" /> View on Maps
            </a>
          </div>
          <div className="studio-detail-block">
            <h2>Studio Hours</h2>
            <dl>
              <div><dt>Mon - Fri</dt><dd>8:00am - 5:30pm</dd></div>
              <div><dt>Saturday</dt><dd>By Appointment</dd></div>
              <div className="closed"><dt>Sunday</dt><dd>Closed</dd></div>
            </dl>
          </div>
          <div className="appointment-card">
            <CalendarCheck size={24} aria-hidden="true" />
            <div>
              <h2>Appointment only</h2>
              <p>
                The studio operates by appointment so each vehicle gets full attention.
                Call {siteConfig.phone} or send a message and we will arrange an assessment.
              </p>
            </div>
          </div>
          <div className="contact-socials" aria-label="Contact channels">
            <a href={`tel:${siteConfig.phone.replace(/ /g, '')}`} aria-label="Call the studio">
              <Phone size={18} />
            </a>
            <a href="/gallery" aria-label="Gallery">
              <Camera size={18} />
            </a>
            <a href={`mailto:${siteConfig.email}`} aria-label="Email">
              <Mail size={18} />
            </a>
            <a href={mapsUrl} aria-label="Directions">
              <Route size={18} />
            </a>
          </div>
        </aside>
      </div>
    </section>
  )
}
